import React, { useState, useEffect } from 'react';
import { format } from 'rut.js';
import { useSnackbar } from 'notistack';
import {
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import axios, { AxiosError } from 'axios';
import handleApiResponse from '../../utils/handleApiResponse';
import { getTuitionsPerPage } from '../../requests/api/contractPayment';
import { useYear } from '../../hooks/useYear';
import Page from '../../components/Layout/Page';
import BaseContractPaymentListView from './ListView';
import DocumentModal from './DocumentModal';
import {
  ContractWithUserData,
  translatePaymentMethod,
  translatePaymentStatus,
} from './types';

function TuitionContractPaymentListView() {
  const { selectedYear } = useYear();
  const { enqueueSnackbar } = useSnackbar();
  const [allContracts, setAllContracts] = useState<{
    items: ContractWithUserData[],
    totalItems: number,
  }>({
    items: [],
    totalItems: 0,
  });
  const [tableParameters, setTableParameters] = useState<{ page: number, rowsPerPage: number }>({
    page: 0,
    rowsPerPage: 10,
  });
  const [searchValue, setSearchValue] = useState<string>('');
  const [filterPaid, setFilterPaid] = useState<string>('');
  const [filterLocation, setFilterLocation] = useState<string>('');
  const [openDocumentModal, setOpenDocumentModal] = useState<boolean>(false);
  const [contractId, setContractId] = useState<string>('');
  const [owedAmount, setOwedAmount] = useState<number | undefined>(undefined);
  const [paidAmount, setPaidAmount] = useState<number | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState<number>(0);

  useEffect(() => {
    const source = axios.CancelToken.source();
    const getAllPayments = async () => {
      try {
        const { tuitions: { rows, count } } = await getTuitionsPerPage({
          page: tableParameters.page,
          limit: tableParameters.rowsPerPage,
          searchValue,
          paidStatus: filterPaid as 'paid' | 'pending' | undefined,
          location: filterLocation as string | undefined,
          year: selectedYear,
        }, source.token);

        const contractsWithFlattenedData = rows.map((contract: ContractWithUserData) => ({
          ...contract,
          name: contract.student?.name ?? '',
          lastname: contract.student?.lastname ?? '',
          rut: format(contract.student?.rut ?? ''),
          status: translatePaymentStatus(contract.paymentStatus ?? ''),
          paymentMethod: translatePaymentMethod(contract.paymentType ?? '-'),
          registerPayment: (
            <IconButton
              onClick={() => handleRegisterPayment(
                contract.id ?? '',
                contract.total - contract.totalPaid,
                contract.totalPaid,
              )}
              sx={{ padding: 0, margin: 0 }}
            >
              {
                contract.total - contract.totalPaid <= 0
                  ? <CheckCircleIcon color="primary" />
                  : <ReceiptIcon color="primary" />
              }
            </IconButton>
          ),
        }));

        setAllContracts({ items: contractsWithFlattenedData, totalItems: count });
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      }
    };

    getAllPayments();

    return () => {
      source.cancel();
    };
  }, [
    tableParameters.page,
    tableParameters.rowsPerPage,
    filterPaid,
    filterLocation,
    searchValue,
    selectedYear,
    refreshKey,
  ]);

  const handleRegisterPayment = (id: string, amountOwed: number, amountPaid: number) => {
    setOpenDocumentModal(true);
    setContractId(id);
    setOwedAmount(amountOwed);
    setPaidAmount(amountPaid);
  };

  return (
    <Page title="Matrículas">
      <BaseContractPaymentListView
        type="tuition"
        title="Matrículas"
        allContracts={allContracts}
        tableParameters={tableParameters}
        searchValue={searchValue}
        filterPaid={filterPaid}
        filterLocation={filterLocation}
        setSearchValue={setSearchValue}
        setFilterPaid={setFilterPaid}
        setFilterLocation={setFilterLocation}
        setTableParameters={setTableParameters}
        selectedYear={selectedYear}
        indicatorsRefreshKey={refreshKey}
      />
      <DocumentModal
        open={openDocumentModal}
        onClose={() => {
          setOpenDocumentModal(false);
          setOwedAmount(undefined);
          setPaidAmount(undefined);
        }}
        onSuccess={() => setRefreshKey((prev) => prev + 1)}
        contractId={contractId}
        type="tuition"
        maxAmount={owedAmount}
        paidAmount={paidAmount}
      />
    </Page>
  );
}

export default TuitionContractPaymentListView;
